import React, { useCallback, useEffect, useRef, useState } from "react";
import FestivalCard from "./components/FestivalCard";
import FestivalShimmerCard from "./components/FestivalShimmerCard";
import FestivalService from "../../api/services/festivalService";
import Breadcrumb from "../../components/Breadcrumb";

const FestivalPage = () => {
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loader = useRef(null);
  const { getFestivals } = FestivalService();
  const [festivals, setFestivals] = useState([]);
  const routes = [{ text: "Festivals" }];

  const fetchFestivals = async (currPage = 1) => {
    try {
      const { result, data } = await getFestivals(currPage);
      if (!result || !data || !Array.isArray(data) || !data.length) {
        setHasMore(false);
        return;
      }
      setFestivals((preValue) => [...preValue, ...data]);
      setPage((preValue) => preValue + 1);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFestivals(page);
  }, []);

  const loadMoreFestivals = async (currentPage) => {
    await fetchFestivals(currentPage);
  };

  const handleObserver = useCallback(
    (entries) => {
      const target = entries[0];
      if (target.isIntersecting && hasMore && !loading) {
        loadMoreFestivals(page + 1);
      }
    },
    [hasMore, loading, page]
  );

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: "20px",
      threshold: 1.0,
    };
    const observer = new IntersectionObserver(handleObserver, options);

    if (loader.current) {
      observer.observe(loader.current);
    }

    return () => {
      if (loader.current) {
        observer.unobserve(loader.current);
      }
    };
  }, [handleObserver]);

  return (
    <>
      <div className="tw-min-h-screen">
        <section className="tw-mt-0 tw-pt-7 tw-mb-6">
          <div className="container-fluid-lg tw-mb-0">
            <Breadcrumb routes={routes} />
          </div>
        </section>
        <section className="blog-section section-b-space tw-mt-0">
          <div className="container-fluid-lg">
            <header className="tw-mb-12 tw-text-center">
              <h1 className="tw-text-4xl md:tw-text-5xl lg:tw-text-6xl tw-font-bold tw-mb-4 tw-text-dark-blue">
                Discover Colorful Festivals
              </h1>
              <p className="tw-text-lg md:tw-text-xl tw-text-dark-blue/80 tw-max-w-2xl tw-mx-auto">
                Dive into the spiritual significance, rituals, and stories of
                celebrated festivals across India. Discover their astrological
                importance and enrich your festive experience with deeper
                meaning.
              </p>
            </header>

            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
              {loading
                ? Array(6)
                    .fill()
                    .map((_, index) => <FestivalShimmerCard key={index} />)
                : festivals.map((festival) => (
                    <FestivalCard
                      key={festival.id}
                      title={festival.title}
                      imageName={festival.imageName}
                      description={festival.description}
                      slug={festival.slug}
                    />
                  ))}
            </div>

            {hasMore && !loading && (
              <div
                ref={loader}
                className="tw-w-full tw-h-20 tw-flex tw-items-center tw-justify-center"
              >
                <div className="tw-animate-pulse tw-bg-dark-blue/20 tw-rounded-full tw-h-12 tw-w-12"></div>
              </div>
            )}

            {!hasMore && !loading && (
              <p className="tw-text-center tw-mt-8 tw-text-gray-600">
                No more festivals to load
              </p>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default FestivalPage;
